'use client';

import { sectionInner } from '../_shared/styles';
import { useBlockPart } from '@akademi/site-builder-runtime';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * Full-width call-to-action strip on brand background.
 *
 * <p>Direct port of ThemeMaker's {@code blocks/cta/CtaBand/CtaBand.tsx}.
 * {@code 'use client'} only for {@link useBlockPart} — still pure-render.
 */
export interface CtaBandProps {
  title?: string;
  subtitle?: string;
  ctaLabel?: string;
  ctaHref?: string;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

export function CtaBand({
  title,
  subtitle,
  ctaLabel,
  ctaHref,
  paddingOuter,
  paddingInner,
}: CtaBandProps) {
  const outerPad = resolvePaddingStyle(paddingOuter);
  const innerPad = resolvePaddingStyle(paddingInner);
  const part = useBlockPart();
  return (
    <BlockRoot
      as="section"
      style={{
        padding: 'var(--space-12, 48px) var(--space-6, 24px)',
        ...outerPad,
        background: 'var(--color-brand-500, #3b5bdb)',
        color: 'var(--color-text-on-brand, #fff)',
      }}
    >
      <div style={{ ...sectionInner, textAlign: 'center', ...innerPad }}>
        <h2
          {...part('title')}
          style={{
            margin: 0,
            fontSize: 'var(--font-size-2xl, 32px)',
            fontWeight: 600,
            fontFamily: 'var(--font-family-display, inherit)',
          }}
        >
          {title || 'Ready?'}
        </h2>
        {subtitle ? (
          <p {...part('subtitle')} style={{ marginTop: 'var(--space-3, 12px)', opacity: 0.9 }}>
            {subtitle}
          </p>
        ) : null}
        {ctaLabel ? (
          <div style={{ marginTop: 'var(--space-6, 24px)' }}>
            <a
              {...part('cta')}
              href={ctaHref ?? '#'}
              style={{
                display: 'inline-block',
                padding: 'var(--space-3, 12px) var(--space-6, 24px)',
                borderRadius: 'var(--radius-md, 8px)',
                background: 'var(--color-neutral-0, #fff)',
                color: 'var(--color-brand-600, #2a45b8)',
                fontWeight: 600,
              }}
            >
              {ctaLabel}
            </a>
          </div>
        ) : null}
      </div>
    </BlockRoot>
  );
}
